/**
 * Left Menu Configuration
 *
 * Centralized configuration for the application's left navigation menu.
 * Menu structure is driven entirely by database tables (menu_sections, menu_categories, menu_items).
 *
 * Every function here is PURE — the area is passed in, never inferred from a
 * module-level mutable. The previous design kept a hidden `preferredSectionKey`
 * that the menu-body resolver WROTE during render and the footer resolver READ,
 * with each matching on different inputs (path vs active tab). They could
 * therefore name different areas for the same frame, which is what put Fund
 * Management items under an "Operations" footer label on a cold load. The area
 * now lives in Redux (store/slices/menuAreaSlice) and both consumers read the
 * same resolved value.
 */

import type { AppIcon } from './iconMap.tsx'
import { getIcon } from './iconMap.tsx'
import { LEFT_MENU_FOOTER_FALLBACK_HREF } from '../state_manager/globals.ts'
import { CONFIG_MODULES_BY_KEY } from '../app_modules/configuration/configModules.ts'

export interface MenuItem {
  key: string
  label: string
  icon: AppIcon
  href: string
}

export interface MenuSection {
  key: string
  title: string
  items: MenuItem[]
  isExpandable?: boolean
  defaultExpanded?: boolean
}

export interface FooterSelectItem {
  key: string
  label: string
  icon: AppIcon
}

export interface MenuConfig {
  sections: MenuSection[]
  footerSelectItems: FooterSelectItem[]
}

export interface MenuConfigData {
  menu_sections?: Array<{ id: string; key: string; name: string; icon: string; default: boolean; sequence: number }>
  menu_categories?: Array<{ id: string; menu_section_id: string; name: string; sequence: number }>
  menu_items?: Array<{
    id: string
    menu_category_id: string
    key: string
    name: string
    icon: string
    href: string
    default: boolean
    sequence: number
    /** cfg.menu_item_visibility_rules key; null/absent = always visible. */
    visibility_rule?: string | null
  }>
}

type MenuItems = NonNullable<MenuConfigData['menu_items']>
type MenuCategories = MenuConfigData['menu_categories']
type MenuSections = MenuConfigData['menu_sections']

/** Per-user facts the visibility rules are evaluated against. */
export interface MenuVisibilityFlags {
  /** cfg.app_admins membership, from GET /global-configs. */
  isAppAdmin: boolean
  /** Any non-trashed row in public.agent_grants, from GET /global-configs. */
  hasConnectedAgent: boolean
}

/**
 * Predicate per cfg.menu_item_visibility_rules key. An item whose rule key is
 * absent from this table is HIDDEN, not shown: cfg can name a rule a shipped
 * client does not implement yet, and failing closed hides a menu row rather
 * than offering one that leads to a screen the user cannot use. Same posture as
 * the is_app_admin gate it sits beside.
 */
const VISIBILITY_RULES: Record<string, (flags: MenuVisibilityFlags) => boolean> = {
  always: () => true,
  app_admin: (flags) => flags.isAppAdmin,
  has_connected_agent: (flags) => flags.hasConnectedAgent
}

function itemPassesVisibilityRule(
  item: { visibility_rule?: string | null },
  flags: MenuVisibilityFlags
): boolean {
  // No rule is the overwhelming majority — every item that predates
  // cfg.menu_items.visibility_rule_id — and means "always".
  const rule = item.visibility_rule
  if (rule == null || rule === '') return true
  return VISIBILITY_RULES[rule]?.(flags) ?? false
}

/**
 * Display-side menu filter. Strips two things:
 *
 *   1. items whose cfg visibility rule this user fails
 *      (cfg.menu_items.visibility_rule_id -> menu_item_visibility_rules.key),
 *   2. Configuration-area items whose panels are admin-only per the
 *      CONFIG_MODULES registry,
 *
 * plus any category left empty by either. (2) is not redundant with (1): the
 * registry flag is also what ConfigSectionPage uses to 403 the route, so it
 * stays the source of truth for those panels until they are migrated onto cfg
 * rules in their own change.
 *
 * DISPLAY ONLY — the /config/* routes 403 for real, and the agent screens are
 * owner-scoped by RLS — so pass the filtered data to the menu body/highlight
 * builders only; area resolution (resolveUnambiguousAreaKey /
 * resolveDisplayAreaKey / readStoredAreaKey) keeps the unfiltered payload so
 * deep links still resolve their area. Returns the input untouched when nothing
 * is stripped (referential equality preserves memoization).
 */
export function filterMenuDataForDisplay(
  menuData: MenuConfigData | null | undefined,
  flags: MenuVisibilityFlags
): MenuConfigData | null | undefined {
  if (!menuData?.menu_items?.length) return menuData

  const items = menuData.menu_items.filter(
    (mi) =>
      itemPassesVisibilityRule(mi, flags) &&
      (flags.isAppAdmin || CONFIG_MODULES_BY_KEY[mi.key]?.adminOnly !== true)
  )
  if (items.length === menuData.menu_items.length) return menuData

  const keptCategoryIds = new Set(items.map((mi) => mi.menu_category_id))
  const categories = (menuData.menu_categories ?? []).filter((c) =>
    keptCategoryIds.has(c.id)
  )
  return { ...menuData, menu_items: items, menu_categories: categories }
}

/**
 * Routes that belong to an area but have no menu item of their own, keyed by
 * FIRST PATH SEGMENT (not by tab module — keying on the tab is what coupled
 * the footer to `useTabManager`'s effect ordering). An entry is honoured only
 * when its area actually exists in cfg, so the table self-heals when sections
 * are renamed or hidden rather than silently resolving to nothing.
 */
const ROUTE_AREA_OVERRIDES: Record<string, string> = {
  'file-viewer': 'fund-management',
  downloads: 'fund-management',
  prospects: 'fund-management'
}

const EMPTY_MENU_CONFIG: MenuConfig = {
  sections: [],
  footerSelectItems: []
}

function sectionKeyForItem(
  item: { menu_category_id: string },
  categories: MenuCategories,
  sections: MenuSections
): string | null {
  const cat = categories?.find((c) => c.id === item.menu_category_id)
  const section = cat ? sections?.find((s) => s.id === cat.menu_section_id) : null
  return section?.key ?? null
}

/**
 * The one area these items belong to, or null when they span several (a
 * duplicated menu key) or none.
 */
function soleSectionKey(
  items: MenuItems,
  categories: MenuCategories,
  sections: MenuSections
): string | null {
  const keys = new Set<string>()
  for (const item of items) {
    const key = sectionKeyForItem(item, categories, sections)
    if (key) keys.add(key)
  }
  return keys.size === 1 ? [...keys][0] : null
}

/**
 * Sections in display order. Copies before sorting — `sections` is the array
 * held in the Redux globalConfigs slice, and sorting it in place mutates store
 * state during render. The key tiebreak matters too: get_global_configs_v3
 * emits menu_sections with no ORDER BY, so equal sequences would otherwise
 * resolve by DB row order.
 */
function orderedSections(sections: MenuSections): NonNullable<MenuSections> {
  return [...(sections ?? [])].sort((a, b) => a.sequence - b.sequence || a.key.localeCompare(b.key))
}

/**
 * The area a route unambiguously belongs to, or null when the route is
 * ambiguous, unmatched, or the menu cfg has not arrived yet.
 *
 * As of 2026-09-02 this feeds the COLD-LOAD SEED only (MainTabContainer's
 * areaSeededRef) — the route no longer steers the area during a session.
 *
 * Takes the path and cfg ONLY — never the active tab. That is what makes the
 * menu body and the footer structurally incapable of disagreeing, and what
 * frees the sidebar from `useTabManager`'s effect ordering (the tab lags the
 * URL by a render, and lags permanently when addModuleTab no-ops on a module
 * that has no defaultTabs entry).
 *
 * Tiers are strictly ordered and the first tier producing ANY match decides.
 * A tier that matches ambiguously returns null rather than falling through —
 * otherwise `/deals` (present in both areas) would skip to a later tier and be
 * answered wrongly instead of leaving the area alone.
 */
export function resolveUnambiguousAreaKey(
  currentPath: string,
  menuData: MenuConfigData | null | undefined
): string | null {
  const sections = menuData?.menu_sections
  const categories = menuData?.menu_categories
  const items = menuData?.menu_items

  // Async window: cfg not loaded. The route is undecidable, not ambiguous —
  // callers treat both the same way (leave the area untouched).
  if (!sections?.length || !categories?.length || !items?.length) return null

  // Tier A — exact href. `/fund-transactions` -> Operations.
  const exact = items.filter((i) => i.href === currentPath)
  if (exact.length) return soleSectionKey(exact, categories, sections)

  // Tier B — longest matching href prefix. This is what resolves record forms:
  // `/fund-transactions/forms/full/<id>` -> `/fund-transactions` -> Operations,
  // while `/funds/forms/full/<id>` -> `/funds` -> both areas -> null (stay put).
  // Prefix only, never equality — equality is Tier A, and re-admitting it here
  // would reopen the ambiguous case.
  const prefixed = items.filter((i) => i.href && currentPath.startsWith(`${i.href}/`))
  if (prefixed.length) {
    const maxLen = Math.max(...prefixed.map((i) => i.href.length))
    return soleSectionKey(
      prefixed.filter((i) => i.href.length === maxLen),
      categories,
      sections
    )
  }

  // Tier C — first-segment override for routes with no menu item at all.
  const segment = currentPath.split('/')[1]
  const override = segment ? ROUTE_AREA_OVERRIDES[segment] : undefined
  if (override && sections.some((s) => s.key === override)) return override

  return null
}

/**
 * The area to actually display: the resolved area when it still exists in cfg,
 * otherwise the default one. The existence check is what makes a stored
 * preference safe — a section that was renamed, deleted or hidden is absent
 * from the delivered payload (the RPC filters is_visible), so it fails here and
 * falls back cleanly instead of rendering an empty menu.
 */
export function resolveDisplayAreaKey(
  areaKey: string | null,
  menuData: MenuConfigData | null | undefined
): string | null {
  const sections = menuData?.menu_sections
  if (!sections?.length) return null
  if (areaKey && sections.some((s) => s.key === areaKey)) return areaKey
  return (sections.find((s) => s.default) ?? orderedSections(sections)[0])?.key ?? null
}

/**
 * That area's default menu item, falling back to its first item by sequence.
 *
 * The footer switcher no longer calls this — picking an area navigates nowhere
 * (owner 2026-09-02). The remaining caller is RootLanding in App.tsx, which
 * redirects '/' to the stored area's default item on login and on the PWA
 * start_url.
 *
 * Resolution is two passes over the area (owner 2026-09-02, the "My Agents
 * reopens itself" report):
 *   1. an item flagged `default` ANYWHERE in the area, categories walked in
 *      sequence order — the cfg flag is the area's landing page and must win
 *      regardless of which category sorts first (MY WORKSPACE sits at
 *      sequence 0 above INVESTING, but Deal Pipeline is the flagged default);
 *   2. only when no item carries the flag, the first item of the first
 *      non-empty category — an area whose first category is empty (or hidden
 *      for this user) must still resolve.
 * Under the old single pass the first category's first item beat a flagged
 * default in a later category, so '/' landed on a closeable personal tab.
 */
export function resolveAreaDefaultHref(
  areaKey: string | null,
  menuData: MenuConfigData | null | undefined
): string {
  const sections = menuData?.menu_sections
  const categories = menuData?.menu_categories
  const items = menuData?.menu_items
  if (!areaKey || !sections?.length || !categories?.length || !items?.length) {
    return LEFT_MENU_FOOTER_FALLBACK_HREF
  }

  const section = sections.find((s) => s.key === areaKey)
  if (!section) return LEFT_MENU_FOOTER_FALLBACK_HREF

  const sectionCategories = categories
    .filter((c) => c.menu_section_id === section.id)
    .sort((a, b) => a.sequence - b.sequence)
  const itemsOf = (catId: string) =>
    items.filter((i) => i.menu_category_id === catId).sort((a, b) => a.sequence - b.sequence)

  for (const cat of sectionCategories) {
    const flagged = itemsOf(cat.id).find((i) => i.default)
    if (flagged?.href) return flagged.href
  }
  for (const cat of sectionCategories) {
    const first = itemsOf(cat.id)[0]
    if (first?.href) return first.href
  }

  return LEFT_MENU_FOOTER_FALLBACK_HREF
}

/**
 * The menu item a path points at WITHIN one area. Scoped to the area, so
 * duplicated keys cannot cross over and no tie-breaking is needed.
 */
function findMenuItemForPathInArea(
  items: MenuItems,
  categories: MenuCategories,
  sections: MenuSections,
  areaKey: string | null,
  currentPath: string
): MenuItems[0] | null {
  if (!areaKey) return null
  const inArea = items.filter((i) => sectionKeyForItem(i, categories, sections) === areaKey)

  const exact = inArea.find((i) => i.href === currentPath)
  if (exact) return exact

  return (
    inArea
      .filter((i) => i.href && currentPath.startsWith(`${i.href}/`))
      .sort((a, b) => b.href.length - a.href.length)[0] ?? null
  )
}

/** AntD Menu `selectedKeys` for the current path within the displayed area. */
export function getSelectedMenuKeys(
  areaKey: string | null,
  currentPath: string,
  menuData: MenuConfigData | null | undefined
): string[] {
  const sections = menuData?.menu_sections
  const categories = menuData?.menu_categories
  const items = menuData?.menu_items
  if (!sections?.length || !categories?.length || !items?.length) return []

  const match = findMenuItemForPathInArea(items, categories, sections, areaKey, currentPath)
  return match ? [match.key] : []
}

/**
 * Builds the rendered menu for one area, plus the footer switcher entries for
 * every area. Depends on (cfg, area) only — not on the path — so ordinary
 * navigation within an area no longer rebuilds the menu.
 */
export function getMenuConfigForArea(
  menuData: MenuConfigData | null | undefined,
  areaKey: string | null
): MenuConfig {
  const sections = menuData?.menu_sections
  const categories = menuData?.menu_categories
  const items = menuData?.menu_items
  if (!sections?.length || !categories?.length || !items?.length) return EMPTY_MENU_CONFIG

  const sectionToShow = areaKey ? sections.find((s) => s.key === areaKey) : undefined
  if (!sectionToShow) return EMPTY_MENU_CONFIG

  // Group once, sort once. These maps hold local arrays, so sorting them is not
  // a Redux mutation — unlike `sections`, which orderedSections() copies.
  const itemsByCategory = new Map<string, MenuItems>()
  for (const item of items) {
    const list = itemsByCategory.get(item.menu_category_id) ?? []
    list.push(item)
    itemsByCategory.set(item.menu_category_id, list)
  }
  for (const list of itemsByCategory.values()) {
    list.sort((a, b) => a.sequence - b.sequence)
  }

  // A category with nothing under it renders as a bare heading, so it is
  // dropped here (owner 2026-09-02). This is the general rule: it fires for
  // admins and non-admins alike, and for a category emptied by cfg
  // `is_visible`, by the admin strip, or by a per-user visibility rule. It has
  // to run AFTER filterMenuDataForDisplay, which is why it lives on the built
  // sections rather than on the raw cfg.
  const menuSections: MenuSection[] = categories
    .filter((c) => c.menu_section_id === sectionToShow.id)
    .sort((a, b) => a.sequence - b.sequence)
    .map((cat) => ({
      key: cat.id,
      title: cat.name,
      items: (itemsByCategory.get(cat.id) ?? []).map((mi) => ({
        key: mi.key,
        label: mi.name,
        icon: getIcon(mi.icon),
        href: mi.href
      }))
    }))
    .filter((section) => section.items.length > 0)

  // No href: picking an area repaints the pane and navigates nowhere
  // (owner 2026-09-02). Carrying a default href here is what let the switcher
  // open an unwanted tab, so the value is not produced at all rather than
  // produced and ignored.
  const footerSelectItems: FooterSelectItem[] = orderedSections(sections).map((sec) => ({
    key: sec.key,
    label: sec.name,
    icon: getIcon(sec.icon)
  }))

  return { sections: menuSections, footerSelectItems }
}
